import React, { useContext, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  listItemClasses,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { warm, neutral, errorAlert, primary } from "style/color";
import { addWeeksToDate, numberRange, shortFormat } from "utils/date-format";
import { moneyFormat } from "utils/money-format";
import { useGetMovementsQuery } from "hooks/use-services/movements.service";
import { getMovementsAPI } from "api/movements.api";
import {
  FUND_MOVEMENT_STATUS_ENUM,
  FUND_MOVEMENT_TYPE_ENUM,
  FundMovementHistory,
} from "types/fund-movements-history";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import { ReactComponent as EyeIcon } from "./../../assets/Icons/eye.svg";
import { ReactComponent as EyeClosedIcon } from "./../../assets/Icons/eye-closed.svg";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import {
  ContributionReceiptsPage,
  ContributionTransactionDetailPageUrl,
  GrantVestmentRequestDetailPageUrl,
  MyCashPageUrl,
  MyDividendDistributionDetailPage,
  MyFeeDetailPage,
  MyGrantsTransactionDetailPagePath,
  MyInvestmentsRequestTransactionDetailPage,
  CashTransferDetailPageUrl,
  FundAdjustmentDetailsPage,
  FundGvDistributionDetailPage,
} from "constants/pages";
import useIntersectionObserver from "hooks/use-services/useIntersectionObserver";
import { generateUrl } from "utils/generateUrl";

interface MovementsCardProps {
  title: string;
  limit: number;
  dashboard?: boolean;
}
export const MovementsCard: React.FC<MovementsCardProps> = ({
  title,
  limit,
  dashboard,
}) => {
  const navigate = useNavigate();
  const { selectedAccount } = useContext<AuthData>(AuthContext);

  const typesFilter = [
    { title: "Contributions", status: FUND_MOVEMENT_TYPE_ENUM.CONTRIBUTION },
    { title: "Grants", status: FUND_MOVEMENT_TYPE_ENUM.GRANT },
    { title: "Investments", status: FUND_MOVEMENT_TYPE_ENUM.INVESTMENT },
    { title: "GrantVestments", status: FUND_MOVEMENT_TYPE_ENUM.GRANTVESTMENT },
    {
      title: "GrantVestments Distribution",
      status: FUND_MOVEMENT_TYPE_ENUM.GV_DISTRIBUTION,
    },
    { title: "Distribution", status: FUND_MOVEMENT_TYPE_ENUM.DISTRIBUTION },
    { title: "Fee", status: FUND_MOVEMENT_TYPE_ENUM.FEE },
    { title: "Adjustment", status: FUND_MOVEMENT_TYPE_ENUM.ADJUSTMENT },
    { title: "Transfer", status: FUND_MOVEMENT_TYPE_ENUM.TRANSFER },
  ];
  const [types, setTypes] = React.useState<string[]>([
    FUND_MOVEMENT_TYPE_ENUM.CONTRIBUTION,
    FUND_MOVEMENT_TYPE_ENUM.GRANT,
    FUND_MOVEMENT_TYPE_ENUM.INVESTMENT,
    FUND_MOVEMENT_TYPE_ENUM.GRANTVESTMENT,
    FUND_MOVEMENT_TYPE_ENUM.GV_DISTRIBUTION,
    FUND_MOVEMENT_TYPE_ENUM.DISTRIBUTION,
    FUND_MOVEMENT_TYPE_ENUM.FEE,
    FUND_MOVEMENT_TYPE_ENUM.ADJUSTMENT,
    FUND_MOVEMENT_TYPE_ENUM.TRANSFER,
  ]);

  const [year, setYear] = React.useState<number>(new Date().getFullYear());
  const availableYears = useMemo(() => {
    const nowYear = new Date().getFullYear();
    const createdAt = selectedAccount?.createdAt;
    const createdDate = createdAt
      ? typeof createdAt === "string"
        ? new Date(createdAt)
        : createdAt
      : null;
    const createdYear =
      createdDate && !isNaN(createdDate.getTime())
        ? createdDate.getFullYear()
        : null;

    if (!createdYear) {
      return numberRange(nowYear, nowYear - 2, -1);
    }

    const start = Math.min(createdYear, nowYear);
    return numberRange(nowYear, start, -1);
  }, [selectedAccount?.createdAt]);

  const handleYearChange = (year: number) => {
    setYear(year);
  };

  useEffect(() => {
    if (availableYears.length === 0) return;
    if (!availableYears.includes(year)) {
      setYear(availableYears[0]);
    }
  }, [availableYears, year]);

  const [endDate] = React.useState<Date>(addWeeksToDate(1));
  const [isDownloading, setIsDownloading] = React.useState(false);

  const {
    data: paginatedData,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetMovementsQuery({
    limit: limit,
    fundId: selectedAccount?.fundAccountId || 0,
    filter: {
      types: types,
    },
    dateTimeRange: {
      startsAt: new Date(`${year}-01-01`),
      endsAt:
        year !== new Date().getFullYear()
          ? new Date(`${year}-12-31T23:59:59.000Z`)
          : endDate,
    },
  });

  const contains = (type: FUND_MOVEMENT_TYPE_ENUM) => {
    return types.includes(type);
  };

  const handleTypeChange = (type: FUND_MOVEMENT_TYPE_ENUM) => {
    setTypes((prev) => {
      if (prev.length === typesFilter.length) {
        return [type];
      }

      const index = prev.findIndex((item) => item === type);

      if (index !== -1) {
        const filterTypes = [...prev];
        filterTypes.splice(index, 1);

        if (filterTypes.length === 0) {
          return typesFilter.map((filter) => filter.status);
        }

        return filterTypes;
      } else {
        return [...prev, type];
      }
    });
  };

  const GoToDetailsPage = (movement: FundMovementHistory) => {
    let url = "";
    switch (movement.type) {
      case FUND_MOVEMENT_TYPE_ENUM.CONTRIBUTION:
        url = ContributionTransactionDetailPageUrl;
        break;
      case FUND_MOVEMENT_TYPE_ENUM.INVESTMENT:
        url = MyInvestmentsRequestTransactionDetailPage;
        break;
      case FUND_MOVEMENT_TYPE_ENUM.GRANTVESTMENT:
        url = GrantVestmentRequestDetailPageUrl;
        break;
      case FUND_MOVEMENT_TYPE_ENUM.GRANT:
        url = MyGrantsTransactionDetailPagePath;
        break;
      case FUND_MOVEMENT_TYPE_ENUM.FEE:
        url = MyFeeDetailPage;
        break;
      case FUND_MOVEMENT_TYPE_ENUM.DISTRIBUTION:
        url = MyDividendDistributionDetailPage;
        break;
      case FUND_MOVEMENT_TYPE_ENUM.GV_DISTRIBUTION:
        url = FundGvDistributionDetailPage;
        break;
      case FUND_MOVEMENT_TYPE_ENUM.ADJUSTMENT:
        url = FundAdjustmentDetailsPage;
        break;

      case FUND_MOVEMENT_TYPE_ENUM.TRANSFER: {
        const cashUrl = generateUrl(CashTransferDetailPageUrl, {
          action: movement.title.includes("To") ? "send" : "receive",
          type: "CASH",
          id: movement.id,
        });

        url = cashUrl;
        break;
      }
    }
    url = url.replace(":id", movement.id.toString());
    navigate(url);
  };

  const movements = useMemo<FundMovementHistory[]>(
    () =>
      paginatedData ? paginatedData?.pages?.flatMap((item) => item.data) : [],
    [paginatedData]
  );
  const lastElementRef = useIntersectionObserver({
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
  });
  const getStatus = (
    status: FUND_MOVEMENT_STATUS_ENUM,
    type: FUND_MOVEMENT_TYPE_ENUM
  ) => {
    switch (status) {
      case FUND_MOVEMENT_STATUS_ENUM.REJECTED:
        return "DECLINED";
      case FUND_MOVEMENT_STATUS_ENUM.ACCEPTED:
        return "APPROVED";
      case FUND_MOVEMENT_STATUS_ENUM.PENDING:
        return type === FUND_MOVEMENT_TYPE_ENUM.GRANT
          ? "PENDING"
          : "IN PROGRESS";
      case FUND_MOVEMENT_STATUS_ENUM.APPROVED:
        return type === FUND_MOVEMENT_TYPE_ENUM.GRANT
          ? "IN PROGRESS"
          : type === FUND_MOVEMENT_TYPE_ENUM.INVESTMENT
            ? "PROCESSED"
            : "APPROVED";
      default:
        return status;
    }
  };

  const getTransactionTypeLabel = (type: FUND_MOVEMENT_TYPE_ENUM) => {
    const labels: Record<FUND_MOVEMENT_TYPE_ENUM, string> = {
      [FUND_MOVEMENT_TYPE_ENUM.CONTRIBUTION]: "Contribution",
      [FUND_MOVEMENT_TYPE_ENUM.GRANT]: "Grant",
      [FUND_MOVEMENT_TYPE_ENUM.INVESTMENT]: "Investment",
      [FUND_MOVEMENT_TYPE_ENUM.GRANTVESTMENT]: "GrantVestment",
      [FUND_MOVEMENT_TYPE_ENUM.GV_DISTRIBUTION]: "GrantVestment Distribution",
      [FUND_MOVEMENT_TYPE_ENUM.DISTRIBUTION]: "Distribution",
      [FUND_MOVEMENT_TYPE_ENUM.FEE]: "Fee",
      [FUND_MOVEMENT_TYPE_ENUM.ADJUSTMENT]: "Adjustment",
      [FUND_MOVEMENT_TYPE_ENUM.TRANSFER]: "Transfer",
    };
    return labels[type] ?? type;
  };

  const getSignedAmountForExcel = (m: FundMovementHistory): number => {
    const rawAmount = m.amount ?? 0;
    const isNegative =
      m.type === FUND_MOVEMENT_TYPE_ENUM.FEE ||
      m.type === FUND_MOVEMENT_TYPE_ENUM.GRANT ||
      m.type === FUND_MOVEMENT_TYPE_ENUM.GRANTVESTMENT ||
      (m.type === FUND_MOVEMENT_TYPE_ENUM.INVESTMENT &&
        m.transactionType === "BUY") ||
      (m.type === FUND_MOVEMENT_TYPE_ENUM.ADJUSTMENT &&
        m.transactionType === "NEGATIVE") ||
      (m.type === FUND_MOVEMENT_TYPE_ENUM.TRANSFER &&
        m.title?.toString().includes("To"));
    const signed = isNegative ? -rawAmount : rawAmount;
    return Math.round(signed * 100) / 100;
  };

  const formatMovementDate = (d: Date | string) => {
    const date = typeof d === "string" ? new Date(d) : d;
    return isNaN(date.getTime()) ? "" : shortFormat(date);
  };

  const handleDownloadExcel = async () => {
    const fundId = selectedAccount?.fundAccountId;
    const fundName = selectedAccount?.fundAccountName ?? "Fund";
    if (!fundId) return;
    setIsDownloading(true);
    try {
      const BATCH = 100;
      let offset = 0;
      let hasNext = true;
      const allMovements: FundMovementHistory[] = [];
      const startsAt = new Date(`${year}-01-01`);
      const endsAt =
        year !== new Date().getFullYear()
          ? new Date(`${year}-12-31T23:59:59.000Z`)
          : endDate;
      const filterTypes =
        types.length === 0 || types.length === typesFilter.length
          ? undefined
          : types;

      while (hasNext) {
        const res = await getMovementsAPI({
          fundId,
          limit: BATCH,
          offset,
          filter: filterTypes ? { types: filterTypes } : undefined,
          dateTimeRange: { startsAt, endsAt },
        });
        allMovements.push(...(res.data ?? []));
        hasNext = res.pagination?.hasNext ?? false;
        offset = res.pagination?.nextOffset ?? offset + BATCH;
      }

      const rows = allMovements.map((m) => ({
        "Transaction Name": m.title ?? "",
        "Authored By": m.user ?? "",
        "Transaction Date": formatMovementDate(m.date),
        "Transaction Type": getTransactionTypeLabel(m.type),
        "Transaction Status": getStatus(m.status, m.type),
        Amount: getSignedAmountForExcel(m),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const colWidths = Object.keys(rows[0] || {}).map((key) => ({
        wch: Math.max(key.length, 14),
      }));
      worksheet["!cols"] = colWidths;
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Cash Transactions");

      const now = new Date();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const yyyy = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, "0");
      const min = String(now.getMinutes()).padStart(2, "0");
      const ss = String(now.getSeconds()).padStart(2, "0");
      const safeName = fundName.replace(/[/\\:*?"<>|]/g, "_");
      const filename = `${safeName}_CashTransactions_${mm}-${dd}-${yyyy}_${hh}${min}${ss}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card sx={{ boxShadow: "none", p: 4 }}>
      <CardContent>
        <Grid container alignItems={"center"}>
          <Grid item xs={12} md={6}>
            <Typography
              sx={{ color: `neutral.main`, mr: 2 }}
              variant="subtitle3"
            >
              {title}
            </Typography>
            {dashboard && (
              <Button
                onClick={() => navigate(MyCashPageUrl)}
                className="link"
                variant="text"
                color="primary"
                size="large"
              >
                See all
              </Button>
            )}
          </Grid>
          <Grid item xs={12} md={6} textAlign={"end"}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: {
                  xs: "flex-start",
                  md: "flex-end",
                },
                gap: 2,
                flexDirection:{
                  xs:"column",
                  md:"row"
                }
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={
                  isDownloading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handleDownloadExcel}
                disabled={isDownloading || !selectedAccount?.fundAccountId}
              >
                {isDownloading ? "Downloading…" : "Download activity into Excel"}
              </Button>
              <Select
                id="title-select"
                size="small"
                name="title"
                value={year}
                onChange={(event) => handleYearChange(+event.target.value)}
              >
                {availableYears.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y === new Date().getFullYear() ? "Current year" : y}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Grid>
        </Grid>

        <Grid sx={{ my: 1 }} container spacing={2}>
          {typesFilter.map((item, index) => {
            return (
              <Grid key={index} item>
                {contains(item.status) ? (
                  <Button
                    key={index}
                    onClick={() => handleTypeChange(item.status)}
                    startIcon={<EyeIcon />}
                    variant={"contained"}
                    color="primary"
                    size="large"
                  >
                    {item.title}
                  </Button>
                ) : (
                  <Button
                    key={index}
                    onClick={() => handleTypeChange(item.status)}
                    startIcon={<EyeClosedIcon />}
                    variant={"outlined"}
                    color="primary"
                    size="large"
                  >
                    {item.title}
                  </Button>
                )}
              </Grid>
            );
          })}
        </Grid>
        {/* Initial loading state */}
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress color="primary" size={40} />
          </Box>
        ) : movements.length !== 0 ? (
          <>
            <List
              className="successor"
              sx={{
                width: "100%",
                bgcolor: "background.paper",
                mt: 2,
                [` & .${listItemClasses.root}:hover`]: {
                  backgroundColor: warm[200],
                  cursor: "pointer",
                },
                opacity: isFetching && !isFetchingNextPage ? 0.7 : 1,
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              {movements.map((movement: any, index: number) => {
                const { status } = movement;
                return (
                  <React.Fragment key={movement.id || index}>
                    <ListItem
                      onClick={() => GoToDetailsPage(movement)}
                      sx={{
                        transition: "background-color 0.2s ease-in-out",
                      }}
                    >
                      <Grid alignItems="center" container>
                        <Grid item xs={12} sm={3} textAlign={"start"}>
                          <ListItemText
                            sx={{
                              display: "inline-grid",
                              width: "auto",
                              flex: "none",
                            }}
                            primary={
                              <Typography
                                sx={{ color: `neutral.main` }}
                                variant="bodyCopyBold"
                                gutterBottom
                              >
                                {movement.title}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                sx={{ color: neutral[600] }}
                                variant="smallCopyBold"
                              >{`By ${movement.user}`}</Typography>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={2} textAlign={"center"}>
                          <Typography
                            variant="bodyCopyRegular"
                            sx={{ color: neutral[600] }}
                          >
                            {shortFormat(movement.date)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3} textAlign={"center"}>
                          {movement.type ===
                          FUND_MOVEMENT_TYPE_ENUM.CONTRIBUTION ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "baseline",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="bodyCopyRegular"
                                sx={{
                                  color: neutral[900],
                                  textTransform: "capitalize",
                                }}
                              >
                                Contribution
                              </Typography>
                              <Button
                                variant="text"
                                color="primary"
                                size="small"
                                sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(ContributionReceiptsPage);
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: "0.7rem",
                                    verticalAlign: "baseline",
                                  }}
                                >
                                  Tax Info
                                </Box>
                              </Button>
                            </Box>
                          ) : (
                            <Typography
                              variant="bodyCopyRegular"
                              sx={{
                                color: neutral[900],
                                textTransform: "capitalize",
                              }}
                            >
                              {movement.type ===
                              FUND_MOVEMENT_TYPE_ENUM.GV_DISTRIBUTION
                                ? "GrantVestment Distribution"
                                : movement.type.toLowerCase()}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={2} textAlign={"center"}>
                          <Button
                            sx={{
                              borderRadius: "4px",
                              p: 1,
                              backgroundColor: warm[200],
                              color: ["IN PROGRESS", "PENDING"].includes(
                                getStatus(status, movement.type)
                              )
                                ? neutral[500]
                                : status ===
                                  FUND_MOVEMENT_STATUS_ENUM.REJECTED ||
                                  status === FUND_MOVEMENT_STATUS_ENUM.CANCELED
                                  ? errorAlert[500]
                                  : primary[500],
                              paddingX: 2,
                              paddingY: 1.5,
                              fontFamily: "Quattrocento Sans",
                              fontSize: "12px",
                              fontWeight: "700",
                              lineHeight: "13.3px",
                            }}
                          >
                            {getStatus(status, movement.type)}
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={2} textAlign={"end"}>
                          <Typography
                            sx={{
                              px: 1,
                              py: 0.5,
                              fontStyle:
                                status === FUND_MOVEMENT_STATUS_ENUM.ACCEPTED &&
                                  movement.type ===
                                  FUND_MOVEMENT_TYPE_ENUM.CONTRIBUTION
                                  ? "italic"
                                  : "",
                              color: !["IN PROGRESS", "PENDING"].includes(
                                getStatus(status, movement.type)
                              )
                                ? neutral[900]
                                : neutral[500],
                              backgroundColor:
                                movement.type ===
                                  FUND_MOVEMENT_TYPE_ENUM.CONTRIBUTION
                                  ? primary[50]
                                  : "transparent",
                              textDecoration:
                                status === FUND_MOVEMENT_STATUS_ENUM.REJECTED ||
                                  status === FUND_MOVEMENT_STATUS_ENUM.CANCELED
                                  ? "line-through"
                                  : "none",
                              borderRadius: 1,
                            }}
                            variant="bodyCopyBold"
                            gutterBottom
                          >
                            <>
                              {movement.type === FUND_MOVEMENT_TYPE_ENUM.FEE ||
                                movement.type === FUND_MOVEMENT_TYPE_ENUM.GRANT ||
                                movement.type ===
                                FUND_MOVEMENT_TYPE_ENUM.GRANTVESTMENT ||
                                (movement.type ===
                                  FUND_MOVEMENT_TYPE_ENUM.INVESTMENT &&
                                  movement.transactionType === "BUY") ||
                                (movement.type ===
                                  FUND_MOVEMENT_TYPE_ENUM.ADJUSTMENT &&
                                  movement.transactionType === "NEGATIVE") ||
                                (movement.type ===
                                  FUND_MOVEMENT_TYPE_ENUM.TRANSFER &&
                                  movement.title.toString().includes("To"))
                                ? `-${moneyFormat(movement.amount)}`
                                : `${moneyFormat(movement.amount)}`}
                            </>
                          </Typography>
                        </Grid>
                      </Grid>
                    </ListItem>
                    {index !== movements.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>

            {!dashboard && (
              <Box ref={lastElementRef}>
                {isFetchingNextPage && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 3,
                      gap: 2,
                    }}
                  >
                    <CircularProgress color="primary" size={24} />
                    <Typography
                      variant="bodyCopyRegular"
                      sx={{ color: neutral[600] }}
                    >
                      Loading more transactions...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        ) : !isLoading ? (
          <Typography
            sx={{ mt: 4, color: neutral[600] }}
            variant="bodyCopyRegular"
          >
            You don't have any past transactions.
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};
